import {
  LoadingIcon,
  ThreatGroupDrillDownPart,
  theme,
  useResponsive,
} from '@clickcease/cc-client-components-lib'
import { Pie } from 'Components/Elements/Chart/Pie'
import { defaultPieOptions } from 'Components/Elements/Chart/Pie/constants'
import { TrendsAndThreatGroups } from 'Components/Elements/Chart/TrendsAndThreatGroups'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { TableRow } from '../ActiveDomainsTable'
import { chartCSS, componentCss, pieCSS, pieSize } from './charts.styles'
import useOverallDomainPerformanceStore from 'Features/home/store/overallDomainPerformance'
import { useUserStore } from 'Stores/userStoreModuleFed'
import { getCountryValueByCountryLabel, toUnixDateRangeNew } from 'Utils/dates'
import { useTrends } from 'Features/home/api/getTrends'
import {
  ReducedResult,
  ReducedThreatGroupsBreakdown,
  ThreatGroupData,
} from 'Features/home/routes/Home/types'
import { filterOutVisitisByStartDate } from '../utils'
import { getUnixTime } from 'date-fns'
import { useThreatGroups } from 'Features/home/api/getThreatGroups'
import { THREAT_GROUPS_COLORS } from 'Features/home/routes/Home/constants'
import { LoaderWrapper } from '../DomainsTable.styled'
import { PiePlaceholderStyled } from 'Components/Elements/Chart/TrendsAndThreatGroups/TrendsAndThreatGroups.styled'
import { AreaSPLineData } from 'Components/Elements/Chart/AreaSPLine/types'

type DomainExpendedDrilldownProps = Pick<TableRow, 'paidMarketingData'> & {
  domain: string
  hasPaidMarketing?: boolean
  hasBotZapping?: boolean
  botMitigationData?: ReducedResult
  isBmLoading?: boolean
}

export const NewDomainExpendedDrilldown = ({
  domain,
  botMitigationData,
  hasBotZapping,
  hasPaidMarketing,
  isBmLoading,
}: DomainExpendedDrilldownProps) => {
  const { t } = useTranslation()
  const { selectedDates } = useOverallDomainPerformanceStore()
  const { timezoneCountry } = useUserStore()

  const bmDataByDomain = useMemo(() => {
    return botMitigationData
      ? filterOutVisitisByStartDate(
          botMitigationData.byDomain[domain],
          selectedDates.from ? getUnixTime(selectedDates.from) : undefined
        )
      : undefined
  }, [botMitigationData, domain, selectedDates.from])

  const selectedTimezone = useMemo(() => {
    return getCountryValueByCountryLabel(timezoneCountry)
  }, [timezoneCountry])

  const serverDateConversion = toUnixDateRangeNew(selectedTimezone, selectedDates)

  const { data: trendsData, isLoading: isTrendsLoading } = useTrends({
    domains: [domain],
    campaigns: [],
    platforms: [],
    startDate: serverDateConversion?.from,
    endDate: serverDateConversion?.to,
    timezone: selectedTimezone,
  })

  const { data: threatGroupsData, isLoading: isThreatGroupsLoading } = useThreatGroups({
    domains: [domain],
    campaigns: [],
    platforms: [],
    startDate: serverDateConversion?.from,
    endDate: serverDateConversion?.to,
    timezone: selectedTimezone,
  })

  const reducedPMThreatsGroupsData: ReducedThreatGroupsBreakdown = useMemo(() => {
    if (!threatGroupsData) return { data: [], totalThreats: 0, drillDownSeries: [] }

    const reducedData = threatGroupsData.reduce(
      (accamulator, current) => {
        accamulator.totalThreats += current.invalid

        const existingGroup = accamulator.data.find((item) => item.name === current.threatGroup)
        // handle group
        if (existingGroup) {
          existingGroup.y += current.invalid
        } else {
          accamulator.data.push({
            name: current.threatGroup,
            drilldown: current.threatGroup,
            y: current.invalid,
            color:
              THREAT_GROUPS_COLORS[current.threatGroup.toLowerCase()] ||
              THREAT_GROUPS_COLORS['invalid bot activity'],
            drilldownData: {
              name: current.threatGroup,
              id: current.threatGroup,
              type: 'pie',
              data: [[current.threatType, current.invalid]],
              dataObj: { [current.threatGroup]: current.invalid },
            },
          })
        }
        // handle type
        const existingType = accamulator.drillDownSeries.find(
          (item) => item.id === current.threatGroup
        )
        if (existingType) {
          existingType.data.push([current.threatType, current.invalid])
          existingType.dataObj[current.threatType] = current.invalid
        } else {
          accamulator.drillDownSeries.push({
            name: current.threatGroup,
            id: current.threatGroup,
            type: 'pie',
            data: [[current.threatType, current.invalid]],
            dataObj: { [current.threatType]: current.invalid },
          })
        }

        return accamulator
      },
      {
        totalThreats: 0,
        data: [] as Array<ThreatGroupData>,
        drillDownSeries: [] as Array<ThreatGroupDrillDownPart>,
      }
    )

    return reducedData
  }, [threatGroupsData])

  const trendData: Array<[number, number]> = useMemo(() => {
    if (!trendsData) return []
    return trendsData.map((trend) => [trend.timestamp * 1000, trend.invalid])
  }, [trendsData])

  const { windowWidth } = useResponsive()
  const totalInvalidVisits = t('total_invalid_visits', { ns: 'home' })

  if (hasPaidMarketing && hasBotZapping) {
    componentCss.gridTemplateColumns = 'repeat(3, 1fr)'
  } else {
    componentCss.gridTemplateColumns = 'repeat(2, 1fr)'
  }

  const computedTrendData = useMemo<AreaSPLineData>(() => {
    const data: AreaSPLineData = [
      { name: '', data: [] },
      { name: '', data: [] },
    ]

    if (hasPaidMarketing) {
      data[0] = {
        name: `${totalInvalidVisits} - ${t('pending_domain_table.paid_marketing', {
          ns: 'dashboard',
        })}`,
        data: trendData,
        fillColor: 'transparent',
        sameYAxis: true,
      }
    }

    if (hasBotZapping) {
      data[1] = {
        name: `${totalInvalidVisits} - ${t('pending_domain_table.bot_zapping', {
          ns: 'dashboard',
        })}`,
        data: bmDataByDomain
          ? bmDataByDomain.visits.map(([first, second]) => [first * 1000, second])
          : [],
        color: theme.colors.chart7.value,
        fillColor: 'transparent',
        percentInTitle: false,
        sameYAxis: true,
      }
    }

    return data
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPaidMarketing, hasBotZapping, totalInvalidVisits, trendData, bmDataByDomain])

  return (
    <TrendsAndThreatGroups
      css={componentCss}
      chartCSS={chartCSS}
      pieCSS={pieCSS}
      pieSize={pieSize}
      threatGroupsBreakdown={reducedPMThreatsGroupsData}
      isChartLoading={isTrendsLoading && isBmLoading}
      isPieLoading={isThreatGroupsLoading}
      areaSPLineData={computedTrendData}
      pieOptions={defaultPieOptions}
    >
      {!isBmLoading && windowWidth && bmDataByDomain && hasBotZapping ? (
        <Pie
          chartCSS={pieCSS}
          total={{
            title: t('total_threats', { ns: 'home' }),
            value: bmDataByDomain ? bmDataByDomain?.totalThreats : 0,
          }}
          data={bmDataByDomain?.data}
          drillDownSeries={bmDataByDomain?.drillDownSeries}
          options={defaultPieOptions}
          pieSize={pieSize}
          isLoading={isBmLoading}
        />
      ) : (
        <PiePlaceholderStyled css={{ flex: '1 1 25%' }}></PiePlaceholderStyled>
      )}
      {isBmLoading && hasBotZapping && (
        <LoaderWrapper css={{ height: '300px', width: '100%', maxWidth: '420px' }}>
          {isBmLoading && <LoadingIcon />}
        </LoaderWrapper>
      )}
    </TrendsAndThreatGroups>
  )
}
